import { useMemo, useRef, useState, type ReactNode } from 'react';
import type { FlowPieceKind, RoutingVersion, VersionStep } from '@arcnet/contracts';
import { useNameVersion } from '../../../lib/api/hooks-tms.js';
import { Badge } from '../../../components/ui/Primitives.js';
import { Icon, type IconName } from '../../../components/ui/Icon.js';
import { KIND_GROUP, KIND_LABEL } from './model.js';
import { describeStep, type Names } from './describe.js';
import { clock, dayLabel, dayMonth, rowsOf, sameDay, shortAt, sourceText, summaryOf, type Row } from './history.js';

/**
 * A route's versions (docs/41 §12, Fable's design): days, newest first, the live one badged, named
 * ones easy to spot. Choosing one shows the routing as it was right after it. Rows wrap: nothing in
 * the list is ever cut short (owner: never hide text).
 */

export const KIND_ICON: Record<FlowPieceKind, IconName> = {
  inbound_route: 'phone', hours: 'clock', ivr: 'ivr', ring_group: 'users', queue: 'queue', message: 'speaker',
};

/** How many rows show before "Show earlier versions". */
const FIRST = 12;

/** Each step a version touched, setting by setting, with any other route it is in. */
export function StepChanges({ steps, names, onStep, note }: {
  steps: VersionStep[]; names: Names;
  onStep?: ((kind: FlowPieceKind, id: string) => void) | undefined;
  /** A note under a step: what its change does elsewhere. */
  note?: ((s: VersionStep) => ReactNode) | undefined;
}) {
  return (
    <>
      {steps.map((s) => {
        const lines = describeStep(s.kind, s.before, s.after, names);
        return (
          <div key={`${s.kind}:${s.id}`} className="arc-history-step">
            <div className="arc-history-step-head">
              <span className="arc-flow-node-icon" data-group={KIND_GROUP[s.kind]}><Icon name={KIND_ICON[s.kind]} size={12} /></span>
              {onStep
                ? <button type="button" className="arc-history-link" onClick={() => onStep(s.kind, s.id)}><strong>{s.name}</strong></button>
                : <strong>{s.name}</strong>}
              <span className="arc-history-step-kind">{KIND_LABEL[s.kind]}</span>
              {s.shared_with.length > 0 && (
                <span className="arc-history-tag"><Icon name="users" size={11} />also in {s.shared_with.join(', ')}</span>
              )}
              <span style={{ marginLeft: 'auto' }}>
                <Badge tone="info"><span className="arc-dot" />{!s.before ? 'new' : !s.after ? 'deleted' : 'changed'}</Badge>
              </span>
            </div>
            <ul className="arc-history-fields">
              {lines.map((l, i) => (
                <li key={i}>{l.text}{l.was !== undefined && <span className="arc-history-was">, was {l.was}</span>}</li>
              ))}
            </ul>
            {note?.(s)}
          </div>
        );
      })}
    </>
  );
}

/** Naming a version, in place: one line, saved for everyone who can see the routing. */
export function NameVersion({ tenantId, version }: { tenantId: string; version: RoutingVersion }) {
  const save = useNameVersion(tenantId);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(version.name ?? '');
  const opener = useRef<HTMLButtonElement>(null);
  const id = `name-${version.id}`;
  const close = () => { setEditing(false); requestAnimationFrame(() => opener.current?.focus()); };

  if (editing) {
    return (
      <form className="arc-history-name-form" onSubmit={(e) => {
        e.preventDefault();
        save.mutate({ id: version.id, name: value.trim() || null }, { onSuccess: close });
      }} onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } }}>
        <label className="arc-label" htmlFor={id}>Name this version</label>
        <div className="arc-row" style={{ gap: 6 }}>
          <input id={id} className="arc-input" value={value} maxLength={80} autoFocus style={{ flex: 1, minWidth: 0 }}
            placeholder="Before December hours" onChange={(e) => setValue(e.target.value)} />
          <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" disabled={save.isPending}>Save</button>
          <button type="button" className="arc-btn arc-btn-sm" onClick={close}>Cancel</button>
        </div>
        <p className="arc-hint" style={{ margin: 0 }}>A name makes a version easy to find again. Everyone who can see the routing sees it.</p>
        {save.error !== null && <p className="arc-error" style={{ margin: 0 }}>{(save.error as Error).message}</p>}
      </form>
    );
  }
  return (
    <div className="arc-history-actions">
      <button ref={opener} type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => { setValue(version.name ?? ''); setEditing(true); }}>
        <Icon name="bookmark" size={12} />{version.name ? 'Rename' : 'Name this version'}
      </button>
      {version.name && (
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={save.isPending}
          onClick={() => save.mutate({ id: version.id, name: null })}>Remove name</button>
      )}
    </div>
  );
}

/** What a row says on its first line: when, who, from where. */
function RowHead({ row, live }: { row: Row; live: boolean }) {
  const v = row.versions[0]!;
  const oldest = row.versions[row.versions.length - 1]!;
  const when = row.versions.length > 1 ? `${clock(oldest.at)}–${clock(v.at)}` : clock(v.at);
  // A count and its word stay together when the line wraps.
  const meta = [v.by, sourceText(v), row.versions.length > 1 ? `${row.versions.length} saves` : null].filter(Boolean).join(' · ');
  return (
    <span className="arc-history-row">
      <span className="arc-history-time">{when}</span>
      <span className="arc-history-meta">· {meta}</span>
      {live && <Badge tone="ok"><span className="arc-dot" />live</Badge>}
    </span>
  );
}

/** One version's whole story: where a restore went back to, each step it touched, each save of a run. */
export function RowDetail({ tenantId, row, names, canName, onStep }: {
  tenantId: string; row: Row; names: Names; canName: boolean;
  onStep?: ((kind: FlowPieceKind, id: string) => void) | undefined;
}) {
  const v = row.versions[0]!;
  return (
    <>
      {v.restored_from && (
        <p className="arc-history-restore-note">
          <Icon name="restore" size={12} />
          Went back to {shortAt(v.restored_from.at)}{v.restored_from.name ? ` · ${v.restored_from.name}` : ''}.
        </p>
      )}
      {v.source === 'start'
        ? <p className="arc-history-restore-note">History starts here: the routing as it was when version history began.</p>
        : <StepChanges steps={row.steps} names={names} onStep={onStep} />}
      {row.versions.length > 1 && (
        <div className="arc-history-each">
          <span className="arc-history-each-head">Each save · restoring this row restores the last</span>
          {row.versions.map((x) => {
            const one = summaryOf({ id: x.id, versions: [x], steps: x.steps }, names);
            return (
              <span key={x.id} className="arc-history-each-row">
                <span className="arc-history-time">{clock(x.at)}</span>
                <span>{one.text}{one.was !== undefined && <span className="arc-history-was">, was {one.was}</span>}</span>
              </span>
            );
          })}
        </div>
      )}
      {canName && <NameVersion key={`${v.id}:${v.name ?? ''}`} tenantId={tenantId} version={v} />}
    </>
  );
}

export function HistoryList({ tenantId, versions, names, viewing, onView, draft, canName, inline = true, onStep, onEscape }: {
  tenantId: string;
  /** Newest first, as the API gives them. */
  versions: RoutingVersion[];
  names: Names;
  /** The version being viewed; null for the routing as it is now. */
  viewing: string | null;
  onView: (versionId: string | null) => void;
  /** Flows: the unpublished draft, which is not a version yet. */
  draft?: { changes: number; savedAt: string | null } | null | undefined;
  canName: boolean;
  /** The chosen row's changes under it (the Flows panel); the Classic window shows them beside the list. */
  inline?: boolean;
  onStep?: ((kind: FlowPieceKind, id: string) => void) | undefined;
  /** Esc with nothing being viewed. */
  onEscape?: (() => void) | undefined;
}) {
  const allRows = useMemo(() => rowsOf(versions), [versions]);
  const [namedOnly, setNamedOnly] = useState(false);
  const [all, setAll] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  const liveId = allRows[0]?.id ?? null;
  const current = viewing ?? liveId;

  const filtered = namedOnly ? allRows.filter((r) => r.versions[0]!.name) : allRows;
  const shown = all ? filtered : filtered.slice(0, FIRST);
  const hidden = filtered.length - shown.length;
  const oldest = versions[versions.length - 1];

  const choose = (r: Row) => onView(r.id === liveId ? null : r.id);
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = [...(list.current?.querySelectorAll<HTMLButtonElement>('.arc-history-item') ?? [])];
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    const go = (i: number) => { e.preventDefault(); items[Math.max(0, Math.min(items.length - 1, i))]?.focus(); };
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(items.length - 1);
    else if (e.key === 'Escape') {
      if (viewing) { e.stopPropagation(); onView(null); } else if (onEscape) { e.stopPropagation(); onEscape(); }
    }
  };

  let lastDay: string | null = null;
  return (
    <>
      <div className="arc-history-filter">
        <button type="button" className="arc-chip arc-chip-toggle" aria-pressed={namedOnly} onClick={() => setNamedOnly((x) => !x)}>
          Named only
        </button>
        <span className="arc-hint" style={{ margin: 0 }}>Every change is a version. Everything is kept.</span>
      </div>
      <div ref={list} className="arc-history-list" role="list" aria-label="Versions" onKeyDown={onKeyDown}>
        {draft && (
          <ul className="arc-history-group" style={{ paddingTop: 8 }}>
            <li>
              <button type="button" className="arc-history-item" data-kind="draft" tabIndex={-1} onClick={() => onView(null)}>
                <span className="arc-history-row">
                  <span className="arc-history-time">Your draft</span>
                  <span className="arc-history-meta">{draft.savedAt ? `· saved ${clock(draft.savedAt)}` : ''}</span>
                  <Badge tone="warn"><span className="arc-dot" />unpublished</Badge>
                </span>
                <span className="arc-history-sum">
                  {draft.changes} {draft.changes === 1 ? 'change' : 'changes'} · becomes a version when you publish it
                </span>
              </button>
            </li>
          </ul>
        )}
        {shown.map((r) => {
          const v = r.versions[0]!;
          const heading = lastDay === null || !sameDay(lastDay, v.at) ? dayLabel(v.at) : null;
          lastDay = v.at;
          const isCurrent = r.id === current;
          const sum = summaryOf(r, names);
          return (
            <div key={r.id}>
              {heading && <h4 className="arc-history-day">{heading}</h4>}
              <ul className="arc-history-group">
                <li>
                  <button type="button" className="arc-history-item" aria-current={isCurrent}
                    aria-expanded={inline ? isCurrent : undefined} data-named={!!v.name || undefined}
                    data-kind={v.source === 'restore' ? 'restore' : r.versions.length > 1 ? 'group' : undefined}
                    tabIndex={isCurrent ? 0 : -1} onClick={() => choose(r)}>
                    {v.name && <span className="arc-history-name"><Icon name="bookmark" size={13} />{v.name}</span>}
                    <RowHead row={r} live={r.id === liveId} />
                    <span className="arc-history-sum">
                      {sum.text}{sum.was !== undefined && <span className="arc-history-was">, was {sum.was}</span>}
                    </span>
                  </button>
                  {inline && isCurrent && (
                    <div className="arc-history-detail">
                      <RowDetail tenantId={tenantId} row={r} names={names} canName={canName} onStep={onStep} />
                    </div>
                  )}
                </li>
              </ul>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="arc-subtle" style={{ padding: '12px 14px', margin: 0, fontSize: 12.5 }}>
            {namedOnly ? 'No named versions yet. Name one from its entry.' : 'No versions yet.'}
          </p>
        )}
        {hidden > 0 && (
          <button type="button" className="arc-history-more" onClick={() => setAll(true)}>
            Show {hidden} earlier {hidden === 1 ? 'version' : 'versions'}
            {oldest ? ` · back to ${dayMonth(oldest.at)}${oldest.source === 'start' ? ', when history began' : ''}` : ''}
          </button>
        )}
      </div>
    </>
  );
}

/** "Main line · since 18 September": the span of history, which folding cannot make wrong. */
export const historySince = (versions: RoutingVersion[]) => {
  const oldest = versions[versions.length - 1];
  return oldest ? `since ${dayMonth(oldest.at)}` : '';
};

export { shortAt };
